import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import AgentLayout from "../components/AgentLayout";
import axiosInstance from "../../../utils/axiosInstance";

function formatNumber(value) {
  const num = Number(value) || 0;
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  }).format(num);
}

function amountColor(value) {
  const num = Number(value) || 0;
  if (num < 0) return { color: "red" };
  if (num > 0) return { color: "green" };
  return {};
}

function DateGroupedFinalSheetTable({ title, count, groups, expandedDates, onToggle }) {
  const totals = groups.reduce(
    (acc, group) => {
      acc.count += Number(group.count) || 0;
      acc.totalMyShare += Number(group.totalMyShare) || 0;
      acc.totalUplineShare += Number(group.totalUplineShare) || 0;
      return acc;
    },
    { count: 0, totalMyShare: 0, totalUplineShare: 0 }
  );

  const totalRowStyle = {
    background: "#1b5e4a",
    color: "#ffffff",
    fontWeight: "bold",
  };

  return (
    <div className="card">
      <div className="card-header">
        <strong className="site_family">
          {title} ({count})
        </strong>
      </div>
      <div className="card-body" style={{ overflowX: "auto" }}>
        <table className="table table-sm table-striped">
          <thead>
            <tr>
              <th className="site_family">Settled</th>
              <th className="site_family">Type</th>
              <th className="site_family">My share</th>
              <th className="site_family">Upline share</th>
              <th className="site_family">Remark</th>
            </tr>
          </thead>
          <tbody>
            {groups.map((group) => {
              const isOpen = Boolean(expandedDates[group.date]);
              return (
                <React.Fragment key={group.date}>
                  <tr onClick={() => onToggle(group.date)} style={{ cursor: "pointer" }}>
                    <td
                      colSpan="5"
                      className="site_family"
                      style={{
                        background: "#02b181",
                        color: "#ffffff",
                        fontWeight: "bold",
                      }}
                    >
                      <i
                        className={`fa ${
                          isOpen ? "fa-chevron-down" : "fa-chevron-right"
                        }`}
                        style={{ marginRight: "8px" }}
                      />
                      {group.date}
                      {"  |  "}
                      Rows: {group.count}
                      {"  |  "}
                      My share: {formatNumber(group.totalMyShare)}
                      {"  |  "}
                      Upline share: {formatNumber(group.totalUplineShare)}
                    </td>
                  </tr>
                  {isOpen &&
                    group.rows.map((row) => (
                      <tr key={row._id}>
                        <td className="site_family">{row.serverDateTime}</td>
                        <td className="site_family">{row.type}</td>
                        <td
                          className="site_family"
                          style={amountColor(row.my_share_amount)}
                        >
                          {formatNumber(row.my_share_amount)}
                        </td>
                        <td
                          className="site_family"
                          style={amountColor(row.upline_share_amount)}
                        >
                          {formatNumber(row.upline_share_amount)}
                        </td>
                        <td className="site_family">{row.remark || "-"}</td>
                      </tr>
                    ))}
                </React.Fragment>
              );
            })}
            {groups.length === 0 && (
              <tr>
                <td colSpan="5" className="site_family">
                  No records found
                </td>
              </tr>
            )}
          </tbody>
          {groups.length > 0 && (
            <tfoot>
              <tr>
                <td className="site_family" style={totalRowStyle}>
                  Total
                </td>
                <td className="site_family" style={totalRowStyle}>
                  {totals.count}
                </td>
                <td className="site_family" style={totalRowStyle}>
                  {formatNumber(totals.totalMyShare)}
                </td>
                <td className="site_family" style={totalRowStyle}>
                  {formatNumber(totals.totalUplineShare)}
                </td>
                <td className="site_family" style={totalRowStyle}></td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}

const CompareSoono = () => {
  const [searchParams] = useSearchParams();
  const uname = searchParams.get("uname") || "soono";

  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [expandedTransDates, setExpandedTransDates] = useState({});
  const [expandedFsDates, setExpandedFsDates] = useState({});
  const [expandedFsNewDates, setExpandedFsNewDates] = useState({});

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      setIsLoading(true);
      setError("");
      try {
        const response = await axiosInstance.post(
          "master/compare-soono-records",
          { uname }
        );
        if (!cancelled) {
          setData(response.data);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.response?.data?.error || "Unable to load comparison records"
          );
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [uname]);

  const transGroups = data?.transDetails?.groups || [];
  const finalGroups = data?.finalSheet?.groups || [];
  const finalNewGroups = data?.finalSheetNew?.groups || [];
  const selfRow = data?.finalSheetSelfRow;
  const timezone = data?.timezone || "Europe/London";

  const transTotals = useMemo(() => {
    return transGroups.reduce(
      (acc, group) => {
        acc.count += Number(group.count) || 0;
        acc.totalAmount += Number(group.totalAmount) || 0;
        acc.totalBettorD += Number(group.totalBettorD) || 0;
        acc.totalMyShare += Number(group.totalMyShare) || 0;
        acc.totalUplineShare += Number(group.totalUplineShare) || 0;
        return acc;
      },
      {
        count: 0,
        totalAmount: 0,
        totalBettorD: 0,
        totalMyShare: 0,
        totalUplineShare: 0,
      }
    );
  }, [transGroups]);

  const toggleTransDate = (date) => {
    setExpandedTransDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const toggleFsDate = (date) => {
    setExpandedFsDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const toggleFsNewDate = (date) => {
    setExpandedFsNewDates((prev) => ({
      ...prev,
      [date]: !prev[date],
    }));
  };

  const summaryCards = useMemo(() => {
    if (!data) return [];
    return [
      {
        title: "bz_bt_punter_trans_details",
        items: [
          ["All rows", data.transDetails.count],
          ["Total amount", formatNumber(data.transDetails.totalAmount)],
          ["Total bettor_d", formatNumber(data.transDetails.totalBettorD)],
          ["Today type 1 rows", data.transDetails.todayType1Count],
          ["Today type 1 amount", formatNumber(data.transDetails.todayType1Amount)],
          ["Today type 1 bettor_d", formatNumber(data.transDetails.todayType1BettorD)],
        ],
      },
      {
        title: "punter_finalsheet_records",
        items: [
          ["All rows", data.finalSheet.count],
          ["Type 1 rows / my / upline", `${data.finalSheet.type1.count} / ${formatNumber(data.finalSheet.type1.my)} / ${formatNumber(data.finalSheet.type1.upline)}`],
          ["Type 2 rows / my / upline", `${data.finalSheet.type2.count} / ${formatNumber(data.finalSheet.type2.my)} / ${formatNumber(data.finalSheet.type2.upline)}`],
          ["Type 3 rows / my / upline", `${data.finalSheet.type3.count} / ${formatNumber(data.finalSheet.type3.my)} / ${formatNumber(data.finalSheet.type3.upline)}`],
        ],
      },
      {
        title: "punter_finalsheet_records_new",
        items: [
          ["All rows", data.finalSheetNew?.count || 0],
          ["Type 1 rows / my / upline", `${data.finalSheetNew?.type1?.count || 0} / ${formatNumber(data.finalSheetNew?.type1?.my)} / ${formatNumber(data.finalSheetNew?.type1?.upline)}`],
          ["Type 2 rows / my / upline", `${data.finalSheetNew?.type2?.count || 0} / ${formatNumber(data.finalSheetNew?.type2?.my)} / ${formatNumber(data.finalSheetNew?.type2?.upline)}`],
          ["Type 3 rows / my / upline", `${data.finalSheetNew?.type3?.count || 0} / ${formatNumber(data.finalSheetNew?.type3?.my)} / ${formatNumber(data.finalSheetNew?.type3?.upline)}`],
        ],
      },
      {
        title: "Final sheet self row (same formula as /agent/final-sheet)",
        items: [
          ["winAmount", formatNumber(selfRow?.winAmount)],
          ["From final sheet my", formatNumber(selfRow?.fromFinalSheetMy)],
          ["From today trans my", formatNumber(selfRow?.fromTodayTransMy)],
          ["From final sheet upline", formatNumber(selfRow?.fromFinalSheetUpline)],
          ["From today trans upline", formatNumber(selfRow?.fromTodayTransUpline)],
        ],
      },
    ];
  }, [data, selfRow]);

  return (
    <AgentLayout>
      <div className="container-fluid">
        <div className="card">
          <div className="card-header">
            <strong className="site_family">
              Compare records — {uname}
            </strong>
            {data?.user?._id && (
              <span className="site_family" style={{ marginLeft: "12px" }}>
                ({data.user._id}) sponsor: {data.user.sponsor || "-"}
              </span>
            )}
            <span className="site_family" style={{ marginLeft: "12px" }}>
              Dates: {timezone}
            </span>
          </div>
          <div className="card-body">
            {isLoading && <p className="site_family">Loading comparison...</p>}
            {error && (
              <p className="site_family" style={{ color: "red" }}>
                {error}
              </p>
            )}

            {!isLoading && !error && (
              <>
                <div className="row">
                  {summaryCards.map((card) => (
                    <div className="col-md-3" key={card.title} style={{ marginBottom: "16px" }}>
                      <div className="card">
                        <div className="card-header">
                          <strong className="site_family">{card.title}</strong>
                        </div>
                        <div className="card-body">
                          <table className="table table-sm">
                            <tbody>
                              {card.items.map(([label, value]) => (
                                <tr key={label}>
                                  <td className="site_family">{label}</td>
                                  <td className="site_family" style={{ fontWeight: "bold" }}>
                                    {value}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>

                <div className="row">
                  <div className="col-md-4">
                    <div className="card">
                      <div className="card-header">
                        <strong className="site_family">
                          bz_bt_punter_trans_details ({data.transDetails.count})
                        </strong>
                      </div>
                      <div className="card-body" style={{ overflowX: "auto" }}>
                        <table className="table table-sm table-striped">
                          <thead>
                            <tr>
                              <th className="site_family">Created</th>
                              <th className="site_family">Type</th>
                              <th className="site_family">Amount</th>
                              <th className="site_family">Bettor D</th>
                              <th className="site_family">My share</th>
                              <th className="site_family">Upline share</th>
                              <th className="site_family">Game / Mkt</th>
                              <th className="site_family">Event / Remark</th>
                            </tr>
                          </thead>
                          <tbody>
                            {transGroups.map((group) => {
                              const isOpen = Boolean(expandedTransDates[group.date]);
                              return (
                                <React.Fragment key={group.date}>
                                  <tr
                                    onClick={() => toggleTransDate(group.date)}
                                    style={{ cursor: "pointer" }}
                                  >
                                    <td
                                      colSpan="8"
                                      className="site_family"
                                      style={{
                                        background: "#02b181",
                                        color: "#ffffff",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      <i
                                        className={`fa ${
                                          isOpen ? "fa-chevron-down" : "fa-chevron-right"
                                        }`}
                                        style={{ marginRight: "8px" }}
                                      />
                                      {group.date}
                                      {"  |  "}
                                      Rows: {group.count}
                                      {"  |  "}
                                      Amount: {formatNumber(group.totalAmount)}
                                      {"  |  "}
                                      Bettor D: {formatNumber(group.totalBettorD)}
                                      {"  |  "}
                                      My share: {formatNumber(group.totalMyShare)}
                                      {"  |  "}
                                      Upline share: {formatNumber(group.totalUplineShare)}
                                    </td>
                                  </tr>
                                  {isOpen &&
                                    group.rows.map((row) => (
                                      <tr key={row._id}>
                                        <td className="site_family">
                                          {row.serverDateTime}
                                        </td>
                                        <td className="site_family">{row.type}</td>
                                        <td
                                          className="site_family"
                                          style={amountColor(row.amount)}
                                        >
                                          {formatNumber(row.amount)}
                                        </td>
                                        <td
                                          className="site_family"
                                          style={amountColor(row.bettor_d)}
                                        >
                                          {formatNumber(row.bettor_d)}
                                        </td>
                                        <td
                                          className="site_family"
                                          style={amountColor(row.amount)}
                                        >
                                          {formatNumber(row.amount)}
                                        </td>
                                        <td
                                          className="site_family"
                                          style={amountColor(row.bettor_d)}
                                        >
                                          {formatNumber(row.bettor_d)}
                                        </td>
                                        <td className="site_family">
                                          {row.game_type ?? "-"} / {row.market_type ?? "-"}
                                        </td>
                                        <td className="site_family">
                                          {row.event_name || row.remark || "-"}
                                        </td>
                                      </tr>
                                    ))}
                                </React.Fragment>
                              );
                            })}
                            {transGroups.length === 0 && (
                              <tr>
                                <td colSpan="8" className="site_family">
                                  No trans details found
                                </td>
                              </tr>
                            )}
                          </tbody>
                          {transGroups.length > 0 && (
                            <tfoot>
                              <tr>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                >
                                  Total
                                </td>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {transTotals.count}
                                </td>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {formatNumber(transTotals.totalAmount)}
                                </td>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {formatNumber(transTotals.totalBettorD)}
                                </td>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {formatNumber(transTotals.totalMyShare)}
                                </td>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                >
                                  {formatNumber(transTotals.totalUplineShare)}
                                </td>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                ></td>
                                <td
                                  className="site_family"
                                  style={{
                                    background: "#1b5e4a",
                                    color: "#ffffff",
                                    fontWeight: "bold",
                                  }}
                                ></td>
                              </tr>
                            </tfoot>
                          )}
                        </table>
                      </div>
                    </div>
                  </div>

                  <div className="col-md-4">
                    <DateGroupedFinalSheetTable
                      title="punter_finalsheet_records"
                      count={data.finalSheet.count}
                      groups={finalGroups}
                      expandedDates={expandedFsDates}
                      onToggle={toggleFsDate}
                    />
                  </div>

                  <div className="col-md-4">
                    <DateGroupedFinalSheetTable
                      title="punter_finalsheet_records_new"
                      count={data.finalSheetNew?.count || 0}
                      groups={finalNewGroups}
                      expandedDates={expandedFsNewDates}
                      onToggle={toggleFsNewDate}
                    />
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </AgentLayout>
  );
};

export default CompareSoono;
